import { IGetOrganization, IOrganization } from '../ts/interface/organization';
import {
  baseUrl,
  createQueryParameters,
  fetchOptions,
  getApplicationId,
  getCredentials,
} from './http';
import { FourOhOneError } from '../actionCreators/system';
import { useOrganizationStore } from '../store/organizationStore';
import CustomToast from '../components/Redesing/custom-toast';

export const fetchOrganization = async (
  organization_id: string
): Promise<IOrganization | undefined> => {
  const { adminEmail, adminToken } = getCredentials();
  const applicationId = getApplicationId();
  const queryParametersObject = {
    application_id: applicationId,
    organization_id: organization_id,
  };
  const queryParameters = createQueryParameters(queryParametersObject);
  const fetchOps = fetchOptions(adminEmail, adminToken) as RequestInit;

  try {
    const response = await fetch(`${baseUrl}/organization?${queryParameters}`, fetchOps);
    const organizationResponse = await response.json();
    if (organizationResponse.status === 200) {
      const { data } = organizationResponse.message;
      return data[0];
    }
    FourOhOneError(organizationResponse);
  } catch (e: any) {
    CustomToast({
      type: 'error',
      message: `Oops! Looks like there is not organization data to show.`,
      duration: 5000,
    });
  }
};

export const getOrganization = async (organization_id: string) => {
  const organization = await fetchOrganization(organization_id);
  if (organization) {
    setOrganization(organization);
  }
};

export const setOrganization = (organization: IOrganization) => {
  const systemAccess = -1;

  const setOrganizationInfo = useOrganizationStore.getState().setOrganizationInfo;

  setOrganizationInfo({
    id: organization.id,
    name: organization.name,
    access: systemAccess,
  });
  // mirror systemGid: remember the last active organization so the next
  // session can restore it
  localStorage.setItem('organizationId', organization.id);
};

export const getAllOrganizations = (params?: IGetOrganization) => {
  const { adminEmail, adminToken } = getCredentials();
  const applicationId = getApplicationId();

  const queryParametersObject = {
    application_id: applicationId,
    ...(params?.organization_id && { organization_id: params.organization_id }),
    ...(params?.limit && { limit: params.limit }),
    ...(params?.step && { step: params.step }),
    ...(params?.value && { value: params.value }),
    ...(params?.search_columns &&
      params?.search_columns.length > 0 && { search_columns: params.search_columns }),
    ...(params?.search_term && { search_term: params.search_term }),
    ...(params?.order_by && { order_by: params.order_by }),
    ...(params?.order_direction && { order_direction: params.order_direction }),
  };

  const queryParameters = createQueryParameters(queryParametersObject);

  const fetchOps = fetchOptions(adminEmail, adminToken) as RequestInit;

  return fetch(`${baseUrl}/organization?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((organizationResponse) => {
      if (organizationResponse.status === 200) {
        return organizationResponse.message;
      }
      FourOhOneError(organizationResponse);
      throw new Error(organizationResponse?.message);
    })
    .catch((e) => {
      CustomToast({
        type: 'error',
        message: `Oops! Looks like there was an error while loading the organizations. Please try again later. ${e}`,
        duration: 5000,
      });
    });
};

export const postOrganizationLogo = async (image: any) => {
  const { adminEmail, adminToken } = getCredentials();
  const applicationId = getApplicationId();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    '',
    'POST',
    applicationId
  ) as RequestInit;

  try {
    const response = await fetch(`${baseUrl}/organization/image`, {
      ...fetchOps,
      body: image,
    });

    return response.json();
  } catch (e: any) {
    CustomToast({
      type: 'error',
      message: `Oops! Looks like there was an error while uploading the image. Please try again later. ${e}`,
      duration: 5000,
    });
  }
};

export const postOrganization = async (organization: any) => {
  const { adminEmail, adminToken } = getCredentials();
  const applicationId = getApplicationId();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    '',
    'POST',
    applicationId
  ) as RequestInit;

  const organizationBody = {
    ...organization,
    application_org: {
      application_id: applicationId,
      config: {
        details: { last_access: new Date().toISOString() },
      },
    },
  };

  const response = await fetch(`${baseUrl}/organization`, {
    ...fetchOps,
    ...(organizationBody && { body: JSON.stringify(organizationBody) }),
  });

  return response.json();
};

export const addRemoveSystemToOrganization = async (
  systemId: string,
  organizationId: string,
  method: 'POST' | 'DELETE'
) => {
  const { adminEmail, adminToken } = getCredentials();
  const applicationId = getApplicationId();

  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    '',
    method,
    applicationId
  ) as RequestInit;

  const response = await fetch(
    `${baseUrl}/organization/${organizationId}/device/${systemId}`,
    fetchOps
  );

  return response.json();
};

export const addUpdateAdminOrganization = async (
  organizationId: string,
  email: string,
  config?: {
    details: { created_on: string; last_access: string; name: string };
  },
  method: 'POST' | 'PUT' | 'DELETE' = 'POST',
  adminId?: string
) => {
  const { adminEmail, adminToken } = getCredentials();
  const applicationId = getApplicationId();

  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    '',
    method,
    applicationId
  ) as RequestInit;

  const url = new URL(
    `${baseUrl}/organization/${organizationId}/admin${adminId ? `/${adminId}` : ''}`
  );

  if (!adminId && email) {
    url.searchParams.append('email', email);
  }

  try {
    const response = await fetch(url.toString(), {
      ...fetchOps,
      body: JSON.stringify({ config }),
    });
    return response.json();
  } catch (e: any) {
    CustomToast({
      type: 'error',
      message: `Oops! Looks like there was an error while ${
        method === 'POST' ? 'adding' : 'updating'
      } the admin. Please try again later. ${e}`,
      duration: 5000,
    });
  }
};
